/* Journal Layout — magazine reading experience.

   Identity vs Editorial / Bold / Minimal:
   • Editorial = dark + cinematic photo hero + serif + italic gold.
   • Bold      = dark + colossal uppercase + magazine cover energy.
   • Minimal   = light + sans-serif + extreme whitespace + lowercase.
   • Journal   = light + MIXED serif/sans + drop-caps + "ISSUE NO" + caption-driven body.

   Best for documentary, wedding, lifestyle photographers. Reads like a
   long-form NYT Magazine feature. */

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme, getSeason } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import IssueSeal from "@/components/editorial/IssueSeal";
import TenantWordmark from "@/components/tenant/TenantWordmark";

const NAV = [
  { to: "/", label: "Home", end: true, num: "00" },
  { to: "/portfolio", label: "Portfolio", num: "01" },
  { to: "/services", label: "Services", num: "02" },
  { to: "/booking", label: "Book", num: "03" },
  { to: "/contact", label: "Contact", num: "04" },
];

export default function JournalLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const location = useLocation();
  const studioName = settings?.studio_name || "Studio";
  const issueNo = String(new Date().getFullYear()).slice(-2);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-stone-100 text-stone-900 antialiased selection:bg-stone-900 selection:text-stone-50">
      {/* ── Masthead bar ───────────────────────────────────────────────── */}
      <header
        className={`sticky top-0 z-40 transition-all duration-500 ${
          scrolled ? "bg-stone-100/90 backdrop-blur-md border-b border-stone-900/15" : "bg-stone-100 border-b border-stone-900/15"
        }`}
        data-testid="journal-masthead"
      >
        {/* Top thin row — magazine masthead */}
        <div className="border-b border-stone-900/10 hidden md:block">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-8 flex items-center justify-between text-[9px] tracking-[0.45em] uppercase text-stone-600">
            <span>Vol. 01 · Issue No. {issueNo}</span>
            <span className="font-serif italic text-[10px] normal-case tracking-normal text-stone-700">
              Photographs and notes from the studio
            </span>
            <span>{new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
          </div>
        </div>

        {/* Main title row */}
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-5 lg:py-7 flex items-center justify-between gap-6">
          <div className="flex-1">
            <Link to={tenantHref("/")} data-testid="journal-wordmark" className="block">
              {settings?.logo_url || settings?.cover_logo_url ? (
                <TenantWordmark
                  logoUrl={settings?.logo_url}
                  coverLogoUrl={settings?.cover_logo_url}
                  tone="light"
                  studioName={studioName}
                  imgClassName="h-12 sm:h-14 lg:h-16 w-auto"
                />
              ) : (
                <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 font-light tracking-tight leading-none">
                  The <span className="italic">{studioName}</span> Journal
                </h1>
              )}
            </Link>
          </div>
          <Link
            to={tenantHref("/booking")}
            className="hidden sm:inline-flex items-center gap-2 px-4 h-9 border border-stone-900/20 text-[10px] tracking-[0.32em] uppercase text-stone-900 hover:bg-stone-900 hover:text-stone-50 transition-colors"
            data-testid="journal-subscribe"
          >
            Enquire
          </Link>
        </div>

        {/* Section nav */}
        <nav className="border-t border-stone-900/15" aria-label="Sections">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-12 flex items-center gap-6 lg:gap-10 overflow-x-auto scrollbar-hidden">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={tenantHref(item.to)}
                end={item.end}
                data-testid={`journal-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative shrink-0 text-[10px] tracking-[0.3em] uppercase py-1.5 transition-colors ${
                    isActive ? "text-stone-900" : "text-stone-500 hover:text-stone-900"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="tabular-nums text-stone-400 mr-2">{item.num}</span>
                    {item.label}
                    {isActive && (
                      <span className="absolute -bottom-px left-0 right-0 h-px bg-stone-900" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main key={location.pathname}>
        <Outlet />
      </main>

      {/* ── Colophon footer ────────────────────────────────────────────── */}
      <footer className="mt-32 lg:mt-44 border-t border-stone-900/15 bg-stone-900 text-stone-200" data-testid="journal-colophon">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="text-[10px] tracking-[0.45em] uppercase text-stone-400 mb-6">Colophon</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-50 font-light leading-tight max-w-2xl">
            <span className="italic">The {studioName} Journal</span> &mdash; published occasionally, when there&apos;s something worth saying.
          </h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 text-sm">
            <ColophonRow label="Studio" value={studioName} />
            <ColophonRow label="Editor" value="The Studio" />
            <ColophonRow label="Email" value={settings?.contact_email || "—"} />
            <ColophonRow label="Where" value={settings?.contact_location || "—"} />
          </div>
          <div className="mt-12 pt-6 border-t border-stone-800 flex flex-wrap items-center justify-between gap-4 text-[10px] tracking-[0.32em] uppercase text-stone-500">
            <span>&copy; {new Date().getFullYear()} {studioName}</span>
            <div className="flex items-center gap-6">
              <LumetierFooterCredit className="hover:text-stone-200" testid="journal-lumetier-credit" />
              <Link to="/admin/login" className="hover:text-stone-200 transition-colors" data-testid="journal-footer-admin">
                Studio Manager
              </Link>
              <span>Issue No. {issueNo}</span>
            </div>
          </div>
        </div>
      </footer>
      {/* Issue seal — masthead corner, charcoal tone on warm canvas */}
      <div className="hidden lg:block fixed top-24 right-6 z-20 pointer-events-none opacity-70" aria-hidden>
        <IssueSeal issue="01" season={getSeason()} year={new Date().getFullYear()} size={88} tone="charcoal" />
      </div>
      <TenantSignature tone="light" />
    </div>
  );
}

function ColophonRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.32em] uppercase text-stone-500 mb-2">{label}</div>
      <div className="font-serif text-base text-stone-100 font-light">{value}</div>
    </div>
  );
}
